import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, FileText, Sparkles, ChevronDown, ChevronRight } from "lucide-react";

// White-label PDF report generator config modal.
// Section keys must match /app/backend/pdf_pro_report.py :: ALL_SECTIONS.
// Only these 7 sections are user-toggleable — the rest (cover_page, kpi_overview,
// detailed_keyword_table, footer_branding) are always included.
const ALL_SECTIONS = [
  { key: "executive_summary",      label: "Executive summary",       desc: "Narrative + at-a-glance stats" },
  { key: "ranking_distribution",   label: "Ranking distribution",    desc: "Where your keywords sit (Top 1–3 → 100+)" },
  { key: "biggest_wins",           label: "Biggest wins",            desc: "Top keywords that improved" },
  { key: "biggest_drops",          label: "Biggest drops",           desc: "Top keywords that lost position" },
  { key: "top_opportunities",      label: "High-value keywords to prioritize next", desc: "Ranked by opportunity score" },
  { key: "activity_timeline",      label: "SEO activity timeline",   desc: "Manual actions logged in this window" },
  { key: "recommendations",        label: "Recommendations",         desc: "Client-friendly next steps" },
];

// Sections always included in the PDF regardless of picker state. Order here
// is preserved when composing the payload (cover → exec → kpi → …).
const ALWAYS_ON_SECTIONS = ["cover_page", "kpi_overview", "detailed_keyword_table", "footer_branding"];

const KEYWORD_COLUMNS = [
  { key: "keyword",              label: "Keyword" },
  { key: "tags",                 label: "Tags" },
  { key: "landing_url",          label: "URL" },
  { key: "engine",               label: "Engine" },
  { key: "country",              label: "Country" },
  { key: "language",             label: "Language" },
  { key: "device",               label: "Device" },
  { key: "search_volume",        label: "Search volume" },
  { key: "previous_rank",        label: "Previous rank" },
  { key: "current_rank",         label: "Current rank" },
  { key: "delta",                label: "Δ Change" },
  { key: "best_rank",            label: "Best rank" },
  { key: "opportunity_score",    label: "Opportunity" },
  { key: "last_updated_at",      label: "Last updated" },
];

const DEFAULT_KEYWORD_COLS = ["keyword", "country", "device", "search_volume", "previous_rank", "current_rank", "delta", "best_rank", "opportunity_score"];

function Section({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E2E8F0] rounded-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F8F9FA] transition"
        data-testid={`pro-section-${title.toLowerCase().replace(/ /g, "-")}`}
      >
        <span className="text-sm font-medium text-[#0A0D14]">{title}</span>
        {open ? <ChevronDown className="h-4 w-4 text-[#8A94A6]" /> : <ChevronRight className="h-4 w-4 text-[#8A94A6]" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-[#E2E8F0] space-y-4">{children}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#0A0D14] mb-1.5">{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[#8A94A6]">{hint}</div>}
    </div>
  );
}

export default function ProReportModal({ project, onClose }) {
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [brand, setBrand] = useState({
    agency_name: "", agency_logo_url: "", agency_website: "",
    agency_contact_email: "", primary_color: "#0047FF",
    prepared_by: "", footer_disclaimer: "",
    logo_mode: "serpnex",  // "serpnex" | "none" | "custom"
  });
  const [client, setClient] = useState({
    client_name: project?.name || "",
    client_logo_url: "",
    client_domain: project?.domain || "",
  });
  const [reportTitle, setReportTitle] = useState("Performance Report");
  const DATE_RANGES = [
    { value: "24h",    label: "Last 24 hours", days: 1 },
    { value: "7d",     label: "Last 7 days",   days: 7 },
    { value: "30d",    label: "Last 30 days",  days: 30 },
    { value: "90d",    label: "Last 90 days",  days: 90 },
    { value: "custom", label: "Custom range…", days: null },
  ];
  const [dateRange, setDateRange] = useState("30d");
  const today = new Date().toISOString().slice(0, 10);
  const defaultStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const [customStart, setCustomStart] = useState(defaultStart);
  const [customEnd, setCustomEnd] = useState(today);
  const period = useMemo(() => {
    if (dateRange === "custom") {
      const sd = new Date(customStart);
      const ed = new Date(customEnd);
      const days = Math.max(1, Math.round((ed - sd) / (1000 * 60 * 60 * 24)));
      const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return {
        date_range: "custom",
        start_date: customStart,
        end_date: customEnd,
        days,
        current_label: `${fmt(sd)} → ${fmt(ed)}`,
        comparison_label: `Baseline ${fmt(sd)}`,
      };
    }
    const r = DATE_RANGES.find((x) => x.value === dateRange) || DATE_RANGES[2];
    return {
      date_range: r.value,
      days: r.days,
      current_label: r.label,
      comparison_label: `Previous ${r.label.replace("Last ", "").toLowerCase()}`,
    };
  }, [dateRange, customStart, customEnd]);
  const [filters, setFilters] = useState({
    tag: "", country: "", language: "", device: "", engine: "",
    sort_by: "opportunity_score",
    max_rows_wins: 10, max_rows_drops: 10, max_rows_opportunities: 10,
    max_rows_detailed: 50, max_rows_activities: 25,
    include_archived: false,
  });
  const [enabledSections, setEnabledSections] = useState(() => new Set(ALL_SECTIONS.map((s) => s.key)));
  const [keywordColumns, setKeywordColumns] = useState(() => new Set(DEFAULT_KEYWORD_COLS));
  const [narrative, setNarrative] = useState("");

  // Prefill agency branding from saved brand_settings
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/brand");
        if (!alive) return;
        // NOTE: agency_name / contact_email / footer_disclaimer are intentionally
        // NOT pre-filled from saved brand — the modal keeps them empty and shows
        // placeholders instead, so the user re-confirms them for each report.
        setBrand((b) => ({
          ...b,
          agency_logo_url: r.data.logo_url || "",
          agency_website: r.data.contact_url || "",
          primary_color: r.data.primary_color || "#0047FF",
        }));
      } catch { /* no brand yet — user can fill inline */ }
    })();
    return () => { alive = false; };
  }, []);

  const toggleSection = (key) => {
    setEnabledSections((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleColumn = (key) => {
    setKeywordColumns((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const buildPayload = () => {
    // Compose sections in canonical PDF order so pages flow correctly:
    // cover → exec → kpi → ranking dist → wins → drops → opportunities →
    // activity timeline → detailed keyword table → recommendations → footer.
    const canonicalOrder = [
      "cover_page",
      "executive_summary",
      "kpi_overview",
      "ranking_distribution",
      "biggest_wins",
      "biggest_drops",
      "top_opportunities",
      "activity_timeline",
      "detailed_keyword_table",
      "recommendations",
      "footer_branding",
    ];
    const active = new Set([...ALWAYS_ON_SECTIONS, ...enabledSections]);
    const sections = canonicalOrder.filter((k) => active.has(k));
    return {
      project_id: project.id,
      report_title: reportTitle,
      sections,
      branding: brand,
      client,
      period,
      filters,
      executive_narrative: narrative.trim() || null,
      keyword_table_columns: Array.from(keywordColumns),
    };
  };

  const generateAiNarrative = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/ai/project-summary/${project.id}`);
      const text = r?.data?.narrative || r?.data?.summary || "";
      if (text) {
        setNarrative(text);
        toast.success("AI narrative generated.");
      } else {
        toast.info("AI narrative unavailable — using template summary.");
      }
    } catch {
      toast.error("AI narrative failed. Template summary will be used instead.");
    } finally { setAiBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("sn_token");
      const resp = await fetch(`${API}/reports/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(buildPayload()),
      });
      if (!resp.ok) {
        let msg = "PDF generation failed.";
        try { const j = await resp.json(); msg = j.detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (project?.domain || "report").replace(/\./g, "-");
      a.download = `${(brand.agency_name || "serpnex").replace(/ /g, "-").toLowerCase()}-${safe}-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error(e.message || "PDF generation failed.");
    } finally { setBusy(false); }
  };

  const sectionsCount = enabledSections.size;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="pro-report-modal">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8 md:my-0 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-display font-semibold text-[#0A0D14]">Generate White-Label Report</div>
            <div className="mt-1 text-sm text-[#525B6A]">
              Configure branding and sections. Produces a client-ready PDF with your logo, KPIs, movement tables, activity summary and recommendations.
            </div>
          </div>
          <button onClick={onClose} data-testid="pro-report-close" className="p-1 text-[#8A94A6] hover:text-[#0A0D14] shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          {/* Agency branding */}
          <Section title="Agency branding">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Agency name">
                <input data-testid="pro-agency-name" value={brand.agency_name} onChange={(e) => setBrand({ ...brand, agency_name: e.target.value })} placeholder="Northlane Agency" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
              <Field label="Prepared by" hint="Optional — appears on the cover">
                <input data-testid="pro-prepared-by" value={brand.prepared_by} onChange={(e) => setBrand({ ...brand, prepared_by: e.target.value })} placeholder="e.g. Amy Chen, Head of SEO" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
            </div>
            <Field label="Header logo" hint="Choose which mark appears in the PDF header on every page.">
              <div className="flex flex-wrap gap-2" data-testid="pro-logo-mode">
                {[
                  { v: "serpnex", label: "Use SerpNex logo" },
                  { v: "none",    label: "No logo" },
                  { v: "custom",  label: "Custom logo URL" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    data-testid={`pro-logo-mode-${opt.v}`}
                    className={`px-3 py-2 rounded-md text-sm border cursor-pointer inline-flex items-center gap-2 ${
                      brand.logo_mode === opt.v
                        ? "border-[#0047FF] bg-[#EEF3FF] text-[#0047FF] font-medium"
                        : "border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="logo_mode"
                      value={opt.v}
                      checked={brand.logo_mode === opt.v}
                      onChange={() => setBrand({ ...brand, logo_mode: opt.v })}
                      className="accent-[#0047FF]"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </Field>
            {brand.logo_mode === "custom" && (
              <Field label="Agency logo URL" hint="Public image URL. PNG or SVG. Max ~2 MB (Size: 512×512 px).">
                <input data-testid="pro-agency-logo" value={brand.agency_logo_url} onChange={(e) => setBrand({ ...brand, agency_logo_url: e.target.value })} placeholder="https://cdn.northlane.com/logo.png" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
            )}
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Website">
                <input data-testid="pro-agency-website" value={brand.agency_website} onChange={(e) => setBrand({ ...brand, agency_website: e.target.value })} placeholder="northlane.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
              <Field label="Contact email">
                <input data-testid="pro-agency-email" value={brand.agency_contact_email} onChange={(e) => setBrand({ ...brand, agency_contact_email: e.target.value })} placeholder="hello@northlane.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
              <Field label="Brand accent">
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="pro-brand-color" value={brand.primary_color} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} className="h-9 w-14 rounded border border-[#E2E8F0] cursor-pointer" />
                  <input value={brand.primary_color} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono" />
                </div>
              </Field>
            </div>
            <Field label="Footer disclaimer">
              <input data-testid="pro-disclaimer" value={brand.footer_disclaimer} onChange={(e) => setBrand({ ...brand, footer_disclaimer: e.target.value })} placeholder="Confidential — for client use only." className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </Field>
          </Section>

          {/* Client details — temporarily hidden per product request. Values still
              default from the project (name + domain) via `client` state so the
              PDF cover/header keeps rendering the client identity. */}
          {/*
          <Section title="Client details" defaultOpen={false}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Client name">
                <input data-testid="pro-client-name" value={client.client_name} onChange={(e) => setClient({ ...client, client_name: e.target.value })} placeholder="Bullionpulse" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
              <Field label="Client domain">
                <input data-testid="pro-client-domain" value={client.client_domain} onChange={(e) => setClient({ ...client, client_domain: e.target.value })} placeholder="bullionpulse.my" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
              </Field>
            </div>
            <Field label="Client logo URL" hint="Optional — appears on cover below the client name">
              <input data-testid="pro-client-logo" value={client.client_logo_url} onChange={(e) => setClient({ ...client, client_logo_url: e.target.value })} placeholder="https://cdn.client.com/logo.png" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </Field>
          </Section>
          */}

          {/* Report period (Report title field removed per product request) */}
          <Section title="Report period">
            <Field label="Date range" hint="Baseline rank + activity timeline both honour this window.">
              <select
                data-testid="pro-date-range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
              >
                {DATE_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {dateRange === "custom" && (
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="pro-custom-range">
                  <div>
                    <label className="block text-[11px] text-[#8A94A6] mb-1">Start date</label>
                    <input
                      type="date"
                      data-testid="pro-custom-start"
                      value={customStart}
                      max={customEnd}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#8A94A6] mb-1">End date</label>
                    <input
                      type="date"
                      data-testid="pro-custom-end"
                      value={customEnd}
                      min={customStart}
                      max={today}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                    />
                  </div>
                </div>
              )}
            </Field>
          </Section>

          {/* Executive narrative */}
          <Section title="Executive summary text" defaultOpen={false}>
            <Field label="Custom narrative" hint="Leave empty for auto-generated template summary. Click 'Rewrite with AI' to draft using Claude.">
              <textarea data-testid="pro-narrative" value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={5} placeholder="During this period we focused on…" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </Field>
            <button
              type="button"
              data-testid="pro-narrative-ai"
              onClick={generateAiNarrative}
              disabled={aiBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-[#0047FF]/40 text-[#0047FF] hover:bg-[#0047FF]/[0.06] disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Rewriting…" : "Rewrite with AI"}
            </button>
          </Section>

          {/* Sections */}
          <Section title={`Report sections (${sectionsCount}/${ALL_SECTIONS.length})`}>
            <div className="grid md:grid-cols-2 gap-3" data-testid="pro-sections-grid">
              {ALL_SECTIONS.map((s) => {
                const on = enabledSections.has(s.key);
                return (
                  <button
                    type="button"
                    key={s.key}
                    data-testid={`pro-section-${s.key}`}
                    onClick={() => toggleSection(s.key)}
                    className={`text-left flex items-start gap-3 px-3 py-2.5 rounded-md border transition ${on ? "border-[#0047FF] bg-[#0047FF]/[0.04]" : "border-[#E2E8F0] hover:border-[#0047FF]/40"}`}
                  >
                    <div className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#0047FF] border-[#0047FF]" : "border-[#8A94A6]"}`}>
                      {on && <span className="text-white text-[10px]">✓</span>}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0A0D14]">{s.label}</div>
                      <div className="text-[11px] text-[#525B6A]">{s.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Keyword table columns */}
          <Section title="Keyword table columns" defaultOpen={false}>
            <div className="flex flex-wrap gap-2" data-testid="pro-columns-grid">
              {KEYWORD_COLUMNS.map((c) => {
                const on = keywordColumns.has(c.key);
                return (
                  <button
                    type="button"
                    key={c.key}
                    data-testid={`pro-col-${c.key}`}
                    onClick={() => toggleColumn(c.key)}
                    className={`px-3 py-1.5 rounded-md text-xs border transition ${on ? "border-[#0047FF] bg-[#0047FF]/[0.06] text-[#0047FF]" : "border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF]/40"}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </Section>
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-between gap-2">
          <div className="text-[11px] text-[#8A94A6]">
            <FileText className="h-3 w-3 inline mr-1 -mt-0.5" />
            The PDF opens for download as soon as it&apos;s ready — no email step.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="pro-cancel" className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button
              onClick={download}
              disabled={busy || sectionsCount === 0}
              data-testid="pro-generate"
              className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              {busy ? "Generating…" : "Generate PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
